import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Users, Building2, Crown, Layers, Settings, Rocket, Landmark, Clock, Boxes, Network, Briefcase, ClipboardCheck } from "lucide-react";
import useHasFeaturedJobs from "@/lib/useHasFeaturedJobs";

const HERO_BG =
  "https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzgzNzk4NjY1fDA&ixlib=rb-4.1.0&q=85";

const ROLE_TYPES = [
  { title: "Executive", icon: Crown, blurb: "CEO, President and C-suite mandates for companies scaling from breakout to category leader.", roles: "CEO · President · CxO" },
  { title: "Platform", icon: Layers, blurb: "Value-creation and platform talent that funds, studios and accelerators embed to accelerate portfolio growth.", roles: "Head of Platform · Talent Partner · Accelerator Director · Program Manager · Portfolio Ops" },
  { title: "Operational", icon: Settings, blurb: "Functional leaders who turn strategy into execution across product, GTM, finance and people.", roles: "VP Product · VP Eng · CFO · CRO" },
  { title: "Venture", icon: Rocket, blurb: "Investors, firm-builders and entrepreneurs-in-residence for funds, studios and accelerators — from analyst to partner.", roles: "Partner · Principal · Analyst · EIR · Investor" },
  { title: "Board", icon: Landmark, blurb: "Independent directors and chairs who bring governance, pattern recognition and network reach.", roles: "Board Member · Board Chair · Independent Director" },
  { title: "Fractional", icon: Clock, blurb: "Part-time and interim leaders who deliver senior expertise without a full-time commitment.", roles: "Fractional CxO · Interim · Advisor" },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Landing() {
  const hasJobs = useHasFeaturedJobs();
  return (
    <div className="min-h-screen bg-[#F9F9FA] text-[#09090B]">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 bg-primary" />
            <span className="font-heading font-black tracking-tight">INNOVATION TALENT PARTNERS</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/for-clients" className="hover:text-[#09090B] transition-colors">For Clients</Link>
            <Link to="/for-candidates" className="hover:text-[#09090B] transition-colors">For Candidates</Link>
            <Link to="/for-recruiters" className="hover:text-[#09090B] transition-colors">For Recruiters</Link>
            <Link to="/advisory-services" className="hover:text-[#09090B] transition-colors">Advisory Services</Link>
            <Link to="/vc-platform" className="hover:text-[#09090B] transition-colors">VC Platform</Link>
            {hasJobs && <Link to="/featured-jobs" className="hover:text-[#09090B] transition-colors">Featured Jobs</Link>}
            <Link to="/our-story" className="hover:text-[#09090B] transition-colors">Our Story</Link>
          </nav>
          <Link
            to="/login"
            data-testid="nav-signin-btn"
            className="text-sm font-medium px-5 py-2 bg-[#09090B] text-white hover:bg-primary transition-colors duration-150"
          >
            Sign In
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-16 min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F9F9FA] via-[#F9F9FA]/90 to-[#F9F9FA]/40" />
          <div className="absolute inset-0 grain opacity-70" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 w-full grid lg:grid-cols-12 gap-8 items-end py-24">
          <motion.div variants={fade} custom={0} initial="hidden" animate="show" className="lg:col-span-8">
            <p className="overline mb-6">Search & Talent Infrastructure · Innovation Economy</p>
            <h1 className="font-heading font-black tracking-tight text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
              Every leader<br />deliberate. Every<br />
              <span className="text-primary">ecosystem built to last.</span>
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-xl">
              We help startups, venture capital firms, accelerators, universities, corporations, and
              economic development organizations recruit exceptional talent and build world-class
              innovation ecosystems.
            </p>
            <div className="mt-10 flex items-center gap-4">
              <Link
                to="/login"
                data-testid="hero-cta-btn"
                className="group flex items-center gap-2 px-7 py-3.5 bg-primary text-white font-medium hover:bg-[#09090B] transition-colors duration-150"
              >
                Enter the platform
                <ArrowUpRight className="h-4 w-4 group-hover:rotate-45 transition-transform duration-200" />
              </Link>
              <a href="#practice" className="text-sm text-muted-foreground hover:text-[#09090B] transition-colors">
                See our practice areas →
              </a>
            </div>
          </motion.div>
          <motion.div variants={fade} custom={2} initial="hidden" animate="show" className="lg:col-span-4 grid grid-cols-2 gap-px bg-black/10 border border-black/10">
            {[
              { k: "Practice areas", v: "6" },
              { k: "Every level", v: "IC → Board" },
              { k: "Two ways we help", v: "Search + Advisory" },
              { k: "Built to last", v: "Ecosystems" },
            ].map((s) => (
              <div key={s.k} className="bg-white p-6">
                <p className="font-heading font-black text-2xl lg:text-3xl leading-tight">{s.v}</p>
                <p className="overline mt-2 text-[0.6rem]">{s.k}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Practice areas */}
      <section id="practice" className="max-w-[1400px] mx-auto px-6 py-24 border-t border-black/10">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="overline mb-3">01 — Practice Areas</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Where we search</h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
          {ROLE_TYPES.map((r, i) => (
            <motion.div
              key={r.title}
              variants={fade}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="group bg-white p-8 hover:bg-[#F1F1F3] transition-colors duration-150"
            >
              <div className="flex items-start justify-between">
                <r.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                <p className="font-heading font-black text-4xl text-black/10 group-hover:text-primary transition-colors duration-200">
                  0{i + 1}
                </p>
              </div>
              <h3 className="font-heading font-bold text-xl mt-5">{r.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{r.blurb}</p>
              <p className="overline text-[0.55rem] mt-4 text-primary/80">{r.roles}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Vision — who we serve */}
      <section id="vision" className="border-t border-black/10 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline mb-3">02 — Our Vision</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight leading-[1.05]">
              The executive search and talent platform for the innovation economy.
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Innovation Talent Partners is a specialized executive search and talent intelligence
              platform connecting innovation organizations with exceptional leadership. We recruit
              executive and strategic talent across the full innovation ecosystem.
            </p>
          </div>
          <div className="lg:col-span-7">
            <p className="overline mb-4">We recruit for</p>
            <div className="grid sm:grid-cols-2 gap-px bg-black/10 border border-black/10">
              {[
                "Venture Capital firms",
                "Accelerators",
                "Incubators",
                "Venture Studios",
                "Economic Development Organizations",
                "Universities",
                "Corporate Innovation Teams",
                "Startup CEOs",
                "Venture-backed startups",
              ].map((org, i) => (
                <div
                  key={org}
                  className="group bg-white px-6 py-5 flex items-center gap-4 hover:bg-[#F1F1F3] transition-colors duration-150"
                >
                  <span className="font-mono text-xs text-muted-foreground group-hover:text-primary transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm font-medium">{org}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Beyond Executive Search — Advisory */}
      <section id="advisory" className="border-t border-black/10 bg-primary text-white overflow-hidden relative">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="max-w-[1400px] mx-auto px-6 py-24 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="overline !text-white/80 mb-3">03 — Beyond Executive Search</p>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight leading-[1.05]">
                Innovation organizations need more than great hires.
              </h2>
              <p className="mt-6 text-white/80 leading-relaxed">
                They need exceptional programs, thriving founder communities, strategic partnerships, and
                experienced leadership. Innovation Talent Partners provides advisory services to help
                organizations launch, grow, and strengthen innovation ecosystems.
              </p>
              <Link
                to="/advisory-services"
                data-testid="cta-advisory"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-white text-primary text-sm font-medium hover:bg-[#09090B] hover:text-white transition-colors duration-150"
              >
                Learn more <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="lg:col-span-7">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/15 border border-white/15">
                {[
                  { icon: Rocket, label: "Accelerator Design" },
                  { icon: Boxes, label: "Incubator Strategy" },
                  { icon: Network, label: "Ecosystem Development" },
                  { icon: Briefcase, label: "Fractional Leadership" },
                  { icon: ClipboardCheck, label: "Program Audits" },
                  { icon: Users, label: "Mentor Network Development" },
                ].map((c, i) => (
                  <motion.div
                    key={c.label}
                    variants={fade}
                    custom={i}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    className="p-6 transition-colors duration-150"
                    style={{ backgroundColor: "#002EA6" }}
                  >
                    <c.icon className="h-6 w-6 text-white" strokeWidth={1.5} />
                    <p className="font-heading font-bold mt-4 leading-tight">{c.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fractional VC Platform */}
      <section id="vc-platform" className="border-t border-black/10 bg-[#F4F4F5]">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="overline mb-3">04 — Fractional VC Platform</p>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight leading-[1.05]">
                World-class platform support, without a full-time hire.
              </h2>
              <p className="mt-6 text-muted-foreground leading-relaxed">
                We embed an experienced platform leader into your fund on a fractional basis — giving your
                portfolio the founder support, talent, fundraising, and programming they need, and giving your
                partners the leverage they want.
              </p>
              <Link
                to="/vc-platform"
                data-testid="cta-vc-platform"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-primary text-white text-sm font-medium hover:bg-[#09090B] transition-colors duration-150"
              >
                Learn more <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="lg:col-span-7">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
                {[
                  { icon: Users, label: "Founder Support" },
                  { icon: Rocket, label: "Talent" },
                  { icon: Landmark, label: "Fundraising" },
                  { icon: Network, label: "Customers & Partnerships" },
                  { icon: Briefcase, label: "Expert Resources" },
                  { icon: Layers, label: "Platform Strategy" },
                ].map((c, i) => (
                  <motion.div
                    key={c.label}
                    variants={fade}
                    custom={i}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    className="bg-white p-6 hover:bg-white transition-colors duration-150"
                  >
                    <c.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                    <p className="font-heading font-bold mt-4 leading-tight text-[#09090B]">{c.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two-column value */}
      <section className="max-w-[1400px] mx-auto px-6 pb-24 grid lg:grid-cols-2 gap-px bg-black/10 border border-black/10">
        <div id="clients" className="bg-white p-10 lg:p-14">
          <Building2 className="h-8 w-8 text-primary" strokeWidth={1.5} />
          <p className="overline mt-6 mb-2">For Clients</p>
          <h3 className="font-heading font-bold text-2xl mb-4">Full visibility into every search</h3>
          <ul className="space-y-3 text-muted-foreground text-sm">
            {["Live pipeline & shortlist visibility", "Interview scheduling & feedback", "Contract and invoice transparency", "Post roles directly to vetted candidates"].map((t) => (
              <li key={t} className="flex gap-3"><span className="text-primary">—</span>{t}</li>
            ))}
          </ul>
          <Link to="/for-clients" data-testid="cta-clients" className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-primary text-white text-sm font-medium hover:bg-[#09090B] transition-colors duration-150">
            Start a partnership <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div id="candidates" className="bg-white p-10 lg:p-14">
          <Users className="h-8 w-8 text-primary" strokeWidth={1.5} />
          <p className="overline mt-6 mb-2">For Candidates</p>
          <h3 className="font-heading font-bold text-2xl mb-4">Opportunities that match your trajectory</h3>
          <ul className="space-y-3 text-muted-foreground text-sm">
            {["Board, fractional & advisory interest signaling", "Curated featured opportunities", "Manage your profile & preferences", "Application status tracking"].map((t) => (
              <li key={t} className="flex gap-3"><span className="text-primary">—</span>{t}</li>
            ))}
          </ul>
          <Link to="/for-candidates" data-testid="cta-candidates" className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-primary text-white text-sm font-medium hover:bg-[#09090B] transition-colors duration-150">
            Create your profile <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 bg-primary" />
            <span className="font-heading font-black text-sm tracking-tight">INNOVATION TALENT PARTNERS</span>
          </div>
          <p className="text-xs text-muted-foreground">© 2026 · Built for the innovation economy.</p>
        </div>
      </footer>
    </div>
  );
}
